/**
 * Robust date parsing that handles multiple formats without timezone shifts.
 * Supports: MM/DD/YYYY, M/D/YYYY, YYYY-MM-DD, and natural language dates.
 */
export function parsePatientDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr || !dateStr.trim()) return null;

  const trimmed = dateStr.trim();

  // MM/DD/YYYY or M/D/YYYY (US format)
  const mdyMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (mdyMatch) {
    const [, m, d, y] = mdyMatch;
    const month = parseInt(m);
    const day = parseInt(d);
    const year = parseInt(y);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const result = new Date(Date.UTC(year, month - 1, day));
    if (result.getUTCMonth() !== month - 1 || result.getUTCDate() !== day) return null;
    return result;
  }

  // YYYY-MM-DD (ISO format)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    const result = new Date(Date.UTC(+y, +m - 1, +d));
    if (result.getUTCMonth() !== +m - 1 || result.getUTCDate() !== +d) return null;
    return result;
  }

  // Fallback: try native parser but anchor to UTC noon to avoid timezone shifts
  const fallback = new Date(trimmed);
  if (!isNaN(fallback.getTime())) return fallback;

  return null;
}

/**
 * Formats a patient DOB string for display (e.g., "April 7, 1954").
 * Returns the fallback if parsing fails.
 */
export function formatPatientDob(dateStr: string | null | undefined, fallback = '—'): string {
  const parsed = parsePatientDate(dateStr);
  if (!parsed) return fallback;
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Normalizes a date string to ISO YYYY-MM-DD format for storage/transmission.
 * Returns null if parsing fails.
 */
export function normalizeDateToISO(dateStr: string | null | undefined): string | null {
  const parsed = parsePatientDate(dateStr);
  if (!parsed) return null;
  const y = parsed.getUTCFullYear();
  const m = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const d = String(parsed.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
